import { type Locale, focusVisible, measures, setLocale, text } from "@quickpoint.me/shared";

import { Authorization } from "../state/authAtoms";
import { DemoMode } from "../state/uiAtoms";
import Divider from "../icons/Divider.svg?react";
import FilesControl from "./controls/FilesControl";
import { IsInPortrait } from "../state/selectors";
import { IsUploading } from "../state/atoms";
import { LocaleAtom } from "../state/localeAtom";
import LogoutControl from "./controls/LogOutControl";
import NewStackControl from "./controls/NewStackControl";
import PresentControl from "./controls/PresentControl";
import PublishControl from "./controls/PublishControl";
import PublishControlFallBack from "./controls/PublishControlFallBack";
import RefreshSpinner from "./Refresh";
import ShareControl from "./controls/ShareControl";
import SignInFromDemoControl from "./controls/SignInFromDemoControl";
import StacksControl from "./controls/StacksControl";
import { Suspense } from "react";
import TknsControl from "./controls/TknsControl";
import TourControl from "./controls/TourControl";
import styled from "styled-components";
import { useRecoilState, useRecoilValue } from "recoil";

const StyledTopBar = styled.header<{ $isInPortrait: boolean }>`
  grid-area: top-bar;
  display: flex;
  align-items: center;
  background-color: ${(props) => props.theme.navigationBackGround};
  border-bottom: 1px solid ${(props) => props.theme.navigationFrame};
  padding-inline-start: ${measures.editor.landscape.gutter};
  padding-inline-end: ${(props) =>
		props.$isInPortrait
			? measures.topBar.portrait.iconMargin
			: measures.topBar.inlineMargin.landscape};

  .logo-area {
    height: 100%;
    display: flex;
    align-items: center;
    flex-shrink: 0;
    column-gap: 0.5rem;
  }
  .spacer {
    flex: 1;
  }
  .controls-area {
    display: flex;
    align-items: center;
    height: 100%;
    width: calc(100svw - ${measures.gridColumns.landscape.right} - ${measures.editor.landscape.gutter});
  }
  .control-group {
    display: flex;
    align-items: center;
    height: 100%;
  }
  /* help/account group sits against the editor–preview boundary */
  .control-group:last-child {
    margin-left: auto;
  }
  /* keep the first label flush with the editor gutter without shrinking
     its hit area: pull the padding outward instead of removing it */
  .control-group:first-child > button:first-child {
    margin-left: calc(-1 * ${measures.topBar.landscape.controlPaddingInline});
  }
  .control-group:last-child > button:last-child {
    margin-right: calc(-1 * ${measures.topBar.landscape.controlPaddingInline});
  }
  .top-bar-divider {
    height: ${(props) => (props.$isInPortrait ? measures.topBar.portrait.icon : measures.topBar.landscape.icon)};
    /* decorative separator: visible against the light bar without shouting */
    color: #999;
    margin: 0 0.5svw;
    flex-shrink: 0;
  }
  .save-spinner {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-left: 1svw;
    svg {
      width: 1.5rem;
    }
  }
  .save-label {
    font-family: ${(props) => props.theme.font};
    font-weight: 400;
    color: #4A4A4A; /* 8.2:1 on the nav surface (AAA) */
    margin-inline-end: .5rem;
  }
  .locale-select {
    background: none;
    /* ≥3:1 against the bar for the non-text contrast rule (WCAG 1.4.11) */
    border: 1px solid #767676;
    border-radius: 4px;
    cursor: pointer;
    padding: 0.3em 0.4em;
    min-height: 48px;
    margin-left: 0.5svw;
    color: #4A4A4A; /* 8.2:1 on the nav surface (AAA) */
    font-family: ${(props) => props.theme.font};
    font-size: 0.8rem;
    font-weight: 500;
    transition: border-color 0.15s ease;

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }

    ${focusVisible}

    &:hover {
      border-color: #333;
      color: #333;
    }
  }
`;

const FloatingStatus = styled.div`
  position: fixed;
  top: 6px;
  right: 6px;
  /* above the editor, below panels/sheets — it must never cover their controls */
  z-index: 40;
  display: flex;
  align-items: center;
  column-gap: 0.5rem;

  .save-label {
    font-size: 0.8rem;
    color: #333;
  }

  .locale-select {
    background: #fff;
    /* ≥3:1 against any backdrop for the non-text contrast rule (WCAG 1.4.11) */
    border: 1px solid #767676;
    border-radius: 6px;
    cursor: pointer;
    padding: 0.3em 0.4em;
    min-height: 44px;
    color: #333;
    font-size: 0.8rem;
    ${focusVisible}
  }
`;

function TopBar() {
	const isInPortrait = useRecoilValue(IsInPortrait);
	const isUploading = useRecoilValue(IsUploading);
	const demoMode = useRecoilValue(DemoMode);
	const isLoggedIn = useRecoilValue(Authorization).isLoggedIn;
	const [locale, setLocaleAtom] = useRecoilState(LocaleAtom);

	const onLocaleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
		const locale = e.target.value as Locale;
		// set the module-level locale and await its translation table BEFORE the
		// atom: subscribers re-render on the atom change and must already see
		// the new translations
		await setLocale(locale);
		setLocaleAtom(locale);
	};

	// portrait: no bar at all — actions live in the bottom bar and its More
	// sheet, so the grid's auto top row collapses and the editor gets the
	// height. Save status (and the dev locale select) float over the tab row's
	// empty right side; only the demo-mode sign-in still warrants a real bar.
	if (isInPortrait) {
		return (
			<>
				{demoMode && !isLoggedIn && (
					<StyledTopBar $isInPortrait={true}>
						<nav className="controls-area" aria-label={text.ui.mainNavigation}>
							<SignInFromDemoControl />
						</nav>
						<div className="spacer" />
					</StyledTopBar>
				)}
				<FloatingStatus>
					<div className="save-spinner" role="status">
						{isUploading && (
							<>
								<div className="save-label">{text.ui.saving}</div>
								<RefreshSpinner whiteSun showBackGround={false} />
							</>
						)}
					</div>
					{import.meta.env.DEV && (
						<select
							className="locale-select"
							value={locale}
							onChange={onLocaleChange}
							aria-label="Language"
						>
							<option value="en" lang="en">English</option>
							<option value="fr" lang="fr">Français</option>
						</select>
					)}
				</FloatingStatus>
			</>
		);
	}

	return (
		<StyledTopBar $isInPortrait={isInPortrait}>
			<nav className="controls-area" aria-label={text.ui.mainNavigation}>
				{!isInPortrait && (
					<>
						<div
							className="control-group"
							role="group"
							aria-label={text.ui.storyGroup}
						>
							<NewStackControl />
							<StacksControl />
							<FilesControl />
						</div>
						<Divider className="top-bar-divider" aria-hidden="true" />
						<Suspense fallback={<PublishControlFallBack />}>
							<div
								className="control-group"
								role="group"
								aria-label={text.ui.sharingGroup}
							>
								<ShareControl />
								<PublishControl />
								<TknsControl />
							</div>
						</Suspense>
						<div
							className="control-group"
							role="group"
							aria-label={text.ui.accountGroup}
						>
							<TourControl />
							<Divider className="top-bar-divider" aria-hidden="true" />
							{!demoMode && isLoggedIn && <LogoutControl />}
							{demoMode && !isLoggedIn && <SignInFromDemoControl />}
						</div>
					</>
				)}
			</nav>
			<div className="spacer" />
			<div className="logo-area">
				{/* role=status so screen readers hear save activity */}
				<div className="save-spinner" role="status">
					{isUploading && (
						<>
							<div className="save-label">{text.ui.saving}</div>
							<RefreshSpinner whiteSun showBackGround={false} />
						</>
					)}
				</div>
				{import.meta.env.DEV && (
					<select
						className="locale-select"
						value={locale}
						onChange={onLocaleChange}
						aria-label="Language"
					>
						<option value="en" lang="en">English</option>
						<option value="fr" lang="fr">Français</option>
					</select>
				)}
				{!isInPortrait && <PresentControl />}
			</div>
		</StyledTopBar>
	);
}

export default TopBar;
